import {User} from "../model/user";
import {
    AtlassianUser,
    Group,
    Label,
    LabelPrefix,
    LabelSearchAPILimit,
    LabelSearchResult,
    Operation,
    PageSearchResponse,
    PageSearchResult
} from "navigation-common/types/confluence-types"
import {jwtstore} from "./JWTStore";
import jwtDecode, {JwtPayload} from "jwt-decode";

export class ConfluenceService {

    static _instance: ConfluenceService;
    readonly MAX_BATCHED_SEARCHES = 10;

    static instance(): ConfluenceService {
        if (!this._instance) {
            this._instance = new ConfluenceService();
        }
        return this._instance;
    }

    public async isCurrentUserAdmin(): Promise<boolean> {
        const currentUser = await this.getCurrentUser();
        return currentUser?.operations?.some((operation: Operation) =>
            operation.operation === "administer" && operation.targetType === "application");
    }

    public async getCurrentUser(): Promise<AtlassianUser> {
        // @ts-ignore
        const data = await AP.request({
            url: '/rest/api/user/current?expand=operations',
            type: 'GET',
            options: this.getJsonOptions()
        });

        return JSON.parse(data.body);
    }

    public getContext(): Promise<string> {
        return new Promise(resolve => {
            // @ts-ignore
            AP.context.getToken(response => resolve(response));
        });
    }

    public async getCurrentSpace(): Promise<string> {
        const context = await AP.context.getContext();
        return context?.confluence?.space?.key ?? "";
    }

    public getCurrentUserAndHost(): User & { clientKey: string } {
        const atlassianJwt = jwtstore.jwt;
        if (atlassianJwt === '') {
            console.log(`Could not determine current user.`);
            return {accountId: "", clientKey: ""};
        }

        const decoded = jwtDecode<JwtPayload>(atlassianJwt);
        return {
            accountId: decoded.sub ?? "",
            clientKey: decoded.iss ?? ""
        }
    }

    /**
     *  Get all groups in the Confluence instance
     *  It is limited to 200 groups by default
     */
    public async getAllGroups(): Promise<Array<Group>> {
        // @ts-ignore
        const data = await AP.request({
            url: '/rest/api/group',
            type: 'GET',
            options: this.getJsonOptions()
        });

        const json = JSON.parse(data.body);
        return json.results ? json.results : [];
    }

    public async getGroupsByQuery(query: string): Promise<Array<Group>> {
        // @ts-ignore
        const data = await AP.request({
            url: `/rest/api/group/picker?query=${query}`,
            type: 'GET',
            options: this.getJsonOptions()
        });

        const json = JSON.parse(data.body);
        return json.results ? json.results : [];
    }

    public async getGroupById(groupId: string): Promise<Group> {
        // @ts-ignore
        const data = await AP.request({
            url: `/rest/api/group/by-id?id=${groupId}`,
            type: 'GET',
            options: this.getJsonOptions()
        });

        return JSON.parse(data.body);
    }

    public async getAllSpaceLabels(): Promise<Array<Label>> {
        // @ts-ignore
        const data = await AP.request({
            url: `/api/v2/labels?prefix=${LabelPrefix.Team}&limit=${LabelSearchAPILimit}`,
            type: 'GET',
            options: this.getJsonOptions()
        });
        const labelSearchResult = JSON.parse(data.body) as LabelSearchResult;
        return labelSearchResult?.results ? labelSearchResult.results : [];
    }

    public getGroupsForCurrentUser(): Promise<Array<Group>> {
        return new Promise<Array<Group>>(async (resolve, reject) => {
            // @ts-ignore
            AP.user.getCurrentUser((user) => {
                // @ts-ignore
                return AP.request({
                    url: `/rest/api/user/memberof?accountId=${user.atlassianAccountId}`,
                    type: 'GET',
                    options: this.getJsonOptions()
                }).then((data: any) => {
                    const json = JSON.parse(data.body);
                    resolve(json.results ? json.results : []);
                }).catch((error: any) => {
                    reject(error);
                });
            });
        })
    }

    public async searchPages(query: string): Promise<PageSearchResult[]> {
        const baseUrl = await this.getBaseUrl();
        // @ts-ignore
        const response = await AP.request({
            url: `/rest/api/content/search?cql=type=page AND title ~ "${query}"&limit=25&expand=container`,
            type: 'GET',
            options: this.getJsonOptions()
        });
        return JSON.parse(response.body).results.map(
            (result: { title: string, id: string, container: { name: string }, _links: { webui: string } }) => ({
                id: result.id,
                title: result.title,
                spaceName: result.container.name,
                link: `${baseUrl}/wiki${result._links.webui}`,
            })
        );
    }

    public async searchByCql(cql: string, next?: string): Promise<PageSearchResponse> {
        const baseUrl = await this.getBaseUrl();
        // @ts-ignore
        const response = await AP.request({
            url: next ?? `/rest/api/content/search?limit=25&expand=container&cql=${cql}`,
            type: 'GET',
            options: this.getJsonOptions()
        });

        const payLoad = JSON.parse(response.body);
        const results = payLoad.results.map(
            (result: { title: string, id: string, container: { name: string }, _links: { webui: string } }) => ({
                id: result.id,
                title: result.title,
                spaceName: result.container.name,
                link: `${baseUrl}/wiki${result._links.webui}`,
            })
        );

        return {
            results,
            start: payLoad.start,
            limit: payLoad.limit,
            size: payLoad.size,
            _links: payLoad._links,
            next: payLoad._links.next
        };
    }

    private getJsonOptions(): object {
        return {
            contentType: "application/json",
            headers: {
                "accept": "application/json"
            }
        }
    }

    public isForbiddenSite = (url: string, target: string) => {
        try {
            const pathname = new URL(url).pathname;
            return this.isSpaceSetting(pathname) || this.isAdminPage(pathname) || this.isMarketplacePage(pathname) || this.isEmbeddedLinkMacro(url) || this.isEmbeddedIn3rdPartyProduct(url) || this.isEmbeddedInMacro();
        } catch (e) {
            console.error("URL is invalid => Hide menu", e);
            return true;
        }
    }

    private isEmbeddedLinkMacro = (url: string) => {
        return new URL(url).searchParams.get("parentProduct") === "smartlink";
    }

    private isEmbeddedInMacro = () => {
        return window.parent !== window.top;
    }

    private isEmbeddedIn3rdPartyProduct = (url: string) => {
        return new URL(url).searchParams.get("parentProduct") === "msteams";
    }

    private getBaseUrl(): Promise<string> {
        return new Promise(
            resolve => AP.navigator.getLocation(
                // @ts-ignore
                (location: { href: string }) => resolve(new URL(location.href).origin)
            )
        );
    }

    private isSpaceSetting = (pathname: string) => {
        return pathname.startsWith("/wiki/spaces") && pathname.includes("/settings/");
    }

    private isAdminPage = (pathname: string) => {
        return pathname.startsWith("/wiki/admin");
    }

    private isMarketplacePage = (pathname: string) => {
        return pathname.startsWith("/wiki/marketplace");
    }
}

export const confluenceService = new ConfluenceService();
